import React, { useMemo } from 'react';
import { Trash2 } from 'lucide-react';

import {
  IAP_APPENDABLE_FORM_IDS,
  IAP_APPENDABLE_FORM_LABELS,
  IAP_BASE_FORM_IDS,
  IAP_FAMILY_CAPABILITIES,
  IAP_FORM_LABELS,
  iapFormLabel,
  orderIapForms,
  type IapAppendableFormId,
  type IapAttachmentManifest,
  type IapBaseFormId,
  type IapFormId,
  type IcsFormFamily,
} from 'components/IAP/iapAttachmentManifest';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const GEOSPATIAL_UPGRADE_TOOLTIP =
  'Upgrade for Premium Automated Geospatial Reporting';

type ExtraFormOption = {
  value: IapAppendableFormId;
  label: string;
};

type IapAttachmentSelectionListProps = {
  manifest: IapAttachmentManifest;
  family: IcsFormFamily;
  disabled?: boolean;
  onChange: (next: IapAttachmentManifest) => void;
};

type SelectionRowProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  /** Why the row cannot be changed, shown on hover. Absent when it can. */
  explanation?: string;
};

const SelectionRow: React.FC<SelectionRowProps> = ({
  id,
  label,
  checked,
  disabled,
  onToggle,
  explanation,
}) => {
  const row = (
    <div
      className="tw:flex tw:items-center tw:gap-2"
      style={explanation ? { opacity: 0.7 } : undefined}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onToggle}
      />
      <Label htmlFor={id} className="tw:font-normal">
        {label}
      </Label>
    </div>
  );

  if (!explanation) return row;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={row} />
        <TooltipContent side="right" className="tw:max-w-xs">
          {explanation}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * The form and attachment checkboxes shared by the ICS-202 card and the IAP
 * cover sheet. A form the active family cannot render is shown disabled rather
 * than hidden, so the printed cover and this list stay recognisable as the same
 * thing.
 */
const IapAttachmentSelectionList: React.FC<IapAttachmentSelectionListProps> = ({
  manifest,
  family,
  disabled = false,
  onChange,
}) => {
  const capability = IAP_FAMILY_CAPABILITIES[family];
  const extraFormAnchor = useComboboxAnchor();

  const supportedForms = useMemo(
    () => new Set(capability.supportedForms),
    [capability.supportedForms]
  );
  const lockedForms = useMemo(
    () => new Set(capability.alwaysIncludedForms),
    [capability.alwaysIncludedForms]
  );
  const selectedForms = useMemo(
    () => new Set(manifest.forms),
    [manifest.forms]
  );

  const extraFormOptions = useMemo<ExtraFormOption[]>(
    () =>
      IAP_APPENDABLE_FORM_IDS.filter((formId) => !selectedForms.has(formId)).map(
        (formId) => ({
          value: formId,
          label: IAP_APPENDABLE_FORM_LABELS[formId],
        })
      ),
    [selectedForms]
  );

  const selectedExtras = useMemo(
    () => IAP_APPENDABLE_FORM_IDS.filter((formId) => selectedForms.has(formId)),
    [selectedForms]
  );

  const setForms = (formIds: readonly IapFormId[]) => {
    onChange({ ...manifest, forms: orderIapForms(formIds) });
  };

  const toggleForm = (formId: IapBaseFormId) => {
    const next = new Set(selectedForms);
    if (next.has(formId)) {
      next.delete(formId);
    } else {
      next.add(formId);
    }
    setForms([...next]);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5" data-tour="iap-components">
      {IAP_BASE_FORM_IDS.map((formId) => {
        const isSupported = supportedForms.has(formId);
        const isLocked = lockedForms.has(formId);

        return (
          <SelectionRow
            key={formId}
            id={`iap-attachment-form-${formId}`}
            label={IAP_FORM_LABELS[formId]}
            checked={selectedForms.has(formId)}
            disabled={disabled || !isSupported || isLocked}
            onToggle={() => toggleForm(formId)}
            explanation={
              isLocked
                ? 'The 202 carries the attachment list, so it is always part of the IAP.'
                : !isSupported
                  ? `${IAP_FORM_LABELS[formId]} is not available in the ${family.toUpperCase()} form set.`
                  : undefined
            }
          />
        );
      })}

      <SelectionRow
        id="iap-attachment-map-chart"
        label="Map / Chart"
        checked={false}
        disabled
        onToggle={() => undefined}
        explanation={GEOSPATIAL_UPGRADE_TOOLTIP}
      />
      <SelectionRow
        id="iap-attachment-weather-tides"
        label="Weather Forecast / Tides / Currents"
        checked={false}
        disabled
        onToggle={() => undefined}
        explanation={GEOSPATIAL_UPGRADE_TOOLTIP}
      />

      {family === 'uscg' && (
        <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-2">
          {selectedExtras.length > 0 && (
            <ul className="tw:flex tw:flex-col tw:gap-1">
              {selectedExtras.map((formId) => (
                <li
                  key={formId}
                  className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
                  data-testid="iap-extra-form"
                >
                  <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                    {iapFormLabel(formId)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label={`Remove ${iapFormLabel(formId)}`}
                    onClick={() =>
                      setForms(manifest.forms.filter((id) => id !== formId))
                    }
                  >
                    <Trash2 className="tw:size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="dark tw:inline-block tw:text-foreground">
            <Combobox
              items={extraFormOptions}
              value={null}
              onValueChange={(next) => {
                const option = next as ExtraFormOption | null;
                if (!option || disabled) return;
                setForms([...manifest.forms, option.value]);
              }}
              itemToStringValue={(item) =>
                item ? (item as ExtraFormOption).label : ''
              }
              disabled={disabled || extraFormOptions.length === 0}
            >
              <div ref={extraFormAnchor}>
                <ComboboxTrigger
                  id="iap-extra-form-combobox"
                  data-testid="iap-extra-form-combobox"
                  render={<Button type="button" variant="outline" size="sm" />}
                  disabled={disabled || extraFormOptions.length === 0}
                >
                  Append another ICS form
                </ComboboxTrigger>
              </div>
              <ComboboxContent
                anchor={extraFormAnchor}
                className="dark tw:w-96 tw:min-w-96 tw:data-[chips=true]:min-w-96"
              >
                <ComboboxInput
                  placeholder="Search forms..."
                  showTrigger={false}
                />
                <ComboboxEmpty>No matches</ComboboxEmpty>
                <ComboboxList>
                  {(item: ExtraFormOption) => (
                    <ComboboxItem
                      key={item.value}
                      value={item}
                      className="tw:whitespace-nowrap"
                    >
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
        </div>
      )}
    </div>
  );
};

export default IapAttachmentSelectionList;
